import * as Sentry from '@sentry/react'

interface ApiRequestOptions extends RequestInit {
  skipAuth?: boolean
}

export class ApiError extends Error {
  status: number
  data?: any

  constructor(message: string, status: number, data?: any) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.data = data
  }
}

export async function apiRequest<T = any>(
  endpoint: string,
  options: ApiRequestOptions = {}
): Promise<T> {
  const { skipAuth = false, ...fetchOptions } = options
  
  // Get the base URL from environment
  const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787'
  const url = `${baseUrl}${endpoint}`
  
  // Create a Sentry span for the API call
  return Sentry.startSpan(
    {
      op: 'http.client',
      name: `${fetchOptions.method || 'GET'} ${endpoint}`,
      attributes: {
        'http.method': fetchOptions.method || 'GET',
        'http.url': url,
      }
    },
    async (span) => {
      try {
        // Get auth token if needed
        let headers: Record<string, string> = {
          'Content-Type': 'application/json',
          ...(fetchOptions.headers as Record<string, string>),
        }
        
        if (!skipAuth) {
          const token = localStorage.getItem('auth_token')
          if (token) {
            headers['Authorization'] = `Bearer ${token}`
          }
        }
        
        // Log the request
        const logger = Sentry.logger
        logger.info('API Request', {
          method: fetchOptions.method || 'GET',
          endpoint,
          hasAuth: !skipAuth && !!localStorage.getItem('auth_token')
        })
        
        const startTime = Date.now()
        const response = await fetch(url, {
          ...fetchOptions,
          headers,
        })
        
        const duration = Date.now() - startTime
        
        // Set span attributes
        span.setAttribute('http.status_code', response.status)
        span.setAttribute('http.response_time', duration)
        
        // Handle response
        const contentType = response.headers.get('content-type')
        let data: any
        
        if (contentType?.includes('application/json')) {
          data = await response.json()
        } else {
          data = await response.text()
        }
        
        if (!response.ok) {
          // Log error response
          logger.error(logger.fmt`API Error: ${response.status} ${response.statusText}`, {
            endpoint,
            status: response.status,
            data
          })
          
          span.setStatus({ code: 2, message: `HTTP ${response.status}` })
          throw new ApiError(
            data?.error || `HTTP ${response.status}: ${response.statusText}`,
            response.status,
            data
          )
        }
        
        // Log successful response
        logger.debug(logger.fmt`API Success: ${endpoint}`, {
          status: response.status,
          duration
        })
        
        return data
      } catch (error) {
        // Capture exception in Sentry
        Sentry.captureException(error, {
          tags: {
            api_endpoint: endpoint,
            api_method: fetchOptions.method || 'GET'
          }
        })
        
        // Re-throw the error
        throw error
      }
    }
  )
}

// Convenience methods
export const api = {
  get: <T = any>(endpoint: string, options?: ApiRequestOptions) =>
    apiRequest<T>(endpoint, { ...options, method: 'GET' }),
    
  post: <T = any>(endpoint: string, data?: any, options?: ApiRequestOptions) =>
    apiRequest<T>(endpoint, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    }),
    
  put: <T = any>(endpoint: string, data?: any, options?: ApiRequestOptions) =>
    apiRequest<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    }),
    
  patch: <T = any>(endpoint: string, data?: any, options?: ApiRequestOptions) =>
    apiRequest<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    }),
    
  delete: <T = any>(endpoint: string, options?: ApiRequestOptions) =>
    apiRequest<T>(endpoint, { ...options, method: 'DELETE' }),
}

// Export logger for use in components
export const logger = Sentry.logger